<!-- =========================================================================================
  File Name: uploads/Immersive360Upload.vue
  Description: Image or video 360 Upload Form. It will create a preview of the uploaded 360 using the immersive-sphere-preview component or video.
========================================================================================== -->

<template>
  <!-- tab 1 content -->
  <form data-vv-scope="step-1">
    <div class="sphere-preview" v-if="url">
      <immersive-sphere-preview
        v-multi-ref:form
        v-bind:hobject="previewHobject"
        @chromaSettingsChanged="onChromaSettingsChanged"
      ></immersive-sphere-preview>
    </div>
    <file-pond
      name="test"
      ref="pond"
      class="filepond-upload"
      :class="{ 'has-preview': url }"
      :maxFileSize="maxFileSize"
      :label-idle="$t('HoverpackImmersiveSphereDrag') + '(Max ' + maxFileSize + ')'"
      :server="{ process, revert, restore, load, fetch }"
      :allow-multiple="false"
      accepted-file-types="image/jpeg, image/png, video/mp4"
      :files="myFiles"
      v-on:removefile="fileRemoved"
      v-on:addfile="fileAdded"
      v-on:init="handleFilePondInit"
      :allowImagePreview="false"
      :allowVideoPreview="false"
    />
    <span class="text-danger">{{ errors.first('step-1.image_upload') }}</span>
    <span v-if="errorMessageUpload" class="text-danger">{{ errorMessageUpload }}</span>
  </form>
</template>

<script>
import humanize from 'string-humanize'
import vueFilePond from 'vue-filepond'
import 'filepond/dist/filepond.min.css'
import FilePondPluginImageExifOrientation from 'filepond-plugin-image-exif-orientation'
import FilePondPluginImageTransform from 'filepond-plugin-image-transform'
import FilePondPluginImageResize from 'filepond-plugin-image-resize'
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type'
import * as HoverlayUtils from '@/assets/js/utils/hoverlay-utils.js'
import imageCompression from 'browser-image-compression'
import ImmersiveSpherePreview from '@/views/hoverlay/components/objects-form/3DViewers/ImmersiveSpherePreview.vue'

const FilePond = vueFilePond(
  FilePondPluginFileValidateType,
  FilePondPluginImageExifOrientation,
  FilePondPluginImageResize,
  FilePondPluginImageTransform
)

export default {
  inject: ['$validator'],
  components: {
    FilePond,
    ImmersiveSpherePreview
  },
  props: {
    hobject: {},
  },
  data() {
    return {
      errorMessageUpload: '',
      myFiles: [],
      url: null,
      previewHobject: {},
    }
  },
  watch: {
    hobject: {
      handler() {
        console.log('hobject changed')
        // Get the current type from hobject abilities
        let currentType = '360image' // default
        try {
          currentType = JSON.parse(this.hobject.abilities).skin.type
        } catch (e) {
          console.warn('Could not parse hobject abilities, using default type')
        }
        this.updatePreviewHobject(this.url, currentType);
      },
      deep: true,
    },
  },
  computed: {
    maxFileSize() {
      return HoverlayUtils.getMaxUploadFileSize()
    },
  },
  created() {
    if (!this.hobject.name) this.hobject.name = ''
    if (!this.hobject.abilities) {
      console.log('No abilities found')
      this.hobject.abilities =
        '{	"interactions": { "start_behavior": "enabled", "orientable_by_user": true, "resizable_by_user": true, "positionable_by_user": true}, "skin": {"auto_start":true,"loop":true,"type":"360image","force_aspect_ratio":false,"optimize_image_size":false,"inner_edge_distance":0.22,"edge_width":0.1,"camera_elevation":2,"radius":10,"grounded":true}}'
    }
    if (this.hobject.asset_uri) {
      // Copy all properties from hobject to previewHobject
      Object.keys(this.hobject).forEach(key => {
        this.$set(this.previewHobject, key, this.hobject[key]);
      });
      this.url = this.hobject.asset_uri
      this.myFiles = [
        {
          source: this.hobject.asset_uri,
          options: {
            type: 'local',
          },
        },
      ]
    }
  },
  async mounted() {
    this.url = this.hobject.asset_uri
  },
  methods: {
    fileRemoved() {
      this.url = null
      // Clear the preview hobject
      this.previewHobject.asset_uri = ''
    },
    fileAdded(error, file) {
      if (!this.hobject.name) {
        this.$set(this.hobject, 'name', humanize(file.filename.replace(/\.[^/.]+$/, '')).substring(0, 63))
      }

      // Determine the type based on the file
      const fileType = file.file.type.includes('image') ? '360image' : '360video'

      // Update abilities with the correct type
      let abilities = JSON.parse(this.hobject.abilities)
      abilities.skin.type = fileType
      this.$set(this.hobject, 'abilities', JSON.stringify(abilities))

      // Create a temporary object URL for preview
      const tempUrl = URL.createObjectURL(file.file)

      this.url = tempUrl

      // Use $nextTick to ensure abilities update is processed before updating preview
      this.$nextTick(() => {
        // Update preview hobject while preserving other properties
        this.updatePreviewHobject(tempUrl, fileType);
      })
    },
    handleFilePondInit() {
    },
    validate() {
      if (this.hobject.data || this.myFiles.length > 0) {
        this.errorMessageUpload = ''
        console.log('[Immersive360Upload.vue] VALIDATION TRUE')
        return true
      } else {
        console.log('[Immersive360Upload.vue] VALIDATION FALSE')
        this.errorMessageUpload = 'You must upload an image'
        return false
      }
    },
    setHobject(hobject) {
      this.hobject = hobject
    },
    async process(fieldName, file, metadata, load, error, progress, abort) {

      let abilities = JSON.parse(this.hobject.abilities)
      if (file.type.includes('image')) {
        abilities.skin.type = '360image'
        const compressedThumbnail = await imageCompression(
          new File([file], file.name, { type: file.type }),
          { maxSizeMB: 1, maxWidthOrHeight: 512, useWebWorker: true }
        )
        this.hobject.image = compressedThumbnail
        const compressedImage = await imageCompression(
          new File([file], file.name, { type: file.type }),
          { maxSizeMB: 5, maxWidthOrHeight: 6000, useWebWorker: true, type: 'image/jpeg' }
        )
        this.hobject.data = compressedImage
      } else {
        abilities.skin.type = '360video'
      }
      this.hobject.abilities = JSON.stringify(abilities)

      this.hobject.data = file

      load(file)
      return {
        abort: () => {
          abort()
        },
      }
    },
    /**
     * Update previewHobject when editing an existing object
     * Preserves all original hobject properties but updates the asset-uri and type
     * @param {String} newAssetUri - The new asset URI to preview
     * @param {String} newAssetType - The type of the new asset ('360image' or '360video')
     */
    updatePreviewHobject(newAssetUri, newAssetType) {
      console.log('updatePreviewHobject', newAssetUri, newAssetType);
      // First, copy all properties from original hobject EXCEPT abilities
      Object.keys(this.hobject).forEach(key => {
        if (key !== 'abilities') {
          this.$set(this.previewHobject, key, this.hobject[key]);
        }
      });

      // Update the asset URI
      this.$set(this.previewHobject, 'asset_uri', newAssetUri);

      // Copy abilities from hobject and ensure the type is set correctly
      let abilities = JSON.parse(this.hobject.abilities)
      if (newAssetType) {
        abilities.skin.type = newAssetType
      }
      this.$set(this.previewHobject, 'abilities', JSON.stringify(abilities))
    },
    onChromaSettingsChanged(chromaSettings) {
      console.log('Chroma settings changed:', chromaSettings)

      // Update hobject abilities with new chroma settings
      let abilities = JSON.parse(this.hobject.abilities)

      // Update all chroma-related settings
      abilities.skin.key_color = chromaSettings.key_color
      abilities.skin.cut_off = chromaSettings.cut_off
      abilities.skin.color_feathering = chromaSettings.color_feathering
      abilities.skin.mask_feathering = chromaSettings.mask_feathering
      abilities.skin.sharpening = chromaSettings.sharpening
      abilities.skin.despill_strength = chromaSettings.despill_strength
      abilities.skin.despill_luminance_add = chromaSettings.despill_luminance_add
      abilities.skin.crop_top = chromaSettings.crop_top
      abilities.skin.crop_bottom = chromaSettings.crop_bottom
      abilities.skin.crop_left = chromaSettings.crop_left
      abilities.skin.crop_right = chromaSettings.crop_right

      // Update hobject
      this.$set(this.hobject, 'abilities', JSON.stringify(abilities))

      // Also update previewHobject to keep them in sync
      this.$set(this.previewHobject, 'abilities', JSON.stringify(abilities))
    },
    load(source, load) {
      fetch(new Request(source)).then(response => {
        response.blob().then(myBlob => {
          load(myBlob)
        })
      })
    },
    fetch(url, load) {
      fetch(new Request(url)).then(response => {
        response.blob().then(myBlob => {
          load(myBlob)
        })
      })
    },
    restore(uniqueFileId, load) {
      // error();
    },
    revert(uniqueFileId, load) {
      // load();
    },
  },
}
</script>

<style lang="scss">
.margin-top {
  margin-top: 25px;
}

.sphere-preview {
  height: auto; /* Set the desired height */
}

// TODO : fix this CSS to hide the video preview properly
// Hide FilePond file items when we have a preview - we only want to show the immersive sphere
.filepond-upload.has-preview {
  ::v-deep .filepond--list-scroller,
  ::v-deep .filepond--item,
  ::v-deep .filepond--file {
    display: none !important;
  }
}
</style>
